import { convertToDateRaw } from "../../helper/helper"
import { buildTransactorFromDetail, Transactor } from "../person/Transactor"
import { SaleItem, buildSaleItem } from "./SaleItem"
import { SaleStatement, buildSaleStatement } from "./SaleStatement"

export class ContractList {
    id: number
    status: string
    market_day?: string
    invoiceId?: number
    sale_items?: SaleItem[]
    sale_id?: number
    contract_type?: string
    statement?: SaleStatement
    transporter?: Transactor
    /** Set when the source reports these directly instead of via line items (ne_contract_list). */
    quantity?: number
    price_per_unit?: number
    delivery_date?: string
    /** Where the supplier sits, from ne_contract_list's supplier.organisation. */
    supplier_name?: string
    village?: string
    subdistrict?: string
    district?: string
    province?: string
    /** `[lng, lat]`, GeoJSON order. Null for every supplier so far. */
    supplier_coordinates?: number[]
    /** Firebase URI of the weight receipt photographed at the gate. */
    weight_receipt_uri?: string
    /**
     * Truck photo and Unilever plot: the first two rungs of the delivery
     * position fallback. The truck photo comes from ne_contract_list
     * `truck_photo`, its GPS from the storage object's metadata; the Unilever
     * plot is not on ne_contract_list yet, so it stays undefined for now.
     */
    truck_photo_uri?: string
    truck_photo_coordinates?: number[]
    unilever_coordinates?: number[]

    constructor(id: number,
        status: string,
        market_day?: string,
        sale_items?: SaleItem[],
        invoiceId?: number,
        sale_id?: number,
        contract_type?: string,
        statement?: SaleStatement,
        transporter?: Transactor) {
        this.id = id
        this.status = status
        this.market_day = market_day
        this.sale_items = sale_items
        this.invoiceId = invoiceId
        this.sale_id = sale_id
        this.contract_type = contract_type
        this.statement = statement
        this.transporter = transporter
    }

    getMarketDay(): string {
        if (this.market_day) {
            return convertToDateRaw(this.market_day, "DD MMMM YYYY")
        } else {
            return "undefined"
        }
    }

    getTotal(): number {
        if (this.quantity !== undefined && this.price_per_unit !== undefined) {
            return this.quantity * this.price_per_unit
        }
        const lineItem = this.sale_items
        if (lineItem) {
            return lineItem.reduce(((value, item) => value + item.getTotal()), 0)
        }
        return null
    }

    getTotalPalmFruit() {
        const lineItem = this.sale_items
        if (lineItem) {
            return lineItem.filter(l => l.isPalmFruit()).reduce(((value, item) => value + item.getTotal()), 0)
        }
        return null
    }

    getQuantity(): number {
        if (this.quantity !== undefined) {
            return this.quantity
        }
        const lineItem = this.sale_items
        if (lineItem) {
            return lineItem.filter(l => l.isPalmFruit()).reduce(((value, item) => value + item.quantity), 0)
        }
        return null
    }

    getPricePerUnit(): number {
        if (this.price_per_unit !== undefined) {
            return this.price_per_unit
        }
        const lineItem = this.sale_items
        const quantity = this.getQuantity()
        if (lineItem && quantity > 0) {
            return this.getTotalPalmFruit() / quantity
        }
        return null
    }

    getDeliveryDate(): string {
        return this.delivery_date ?? this.sale_items?.[this.sale_items.length - 1]?.createdAt
    }

    isNFAContract() {
        // NAM, NAM_VIPM and NAM_VIPP are all NAM contracts
        return this.contract_type?.startsWith("NAM") === true
    }

    isNac() {
        return this.contract_type === "NAC"
    }
}

export function buildContractListForMarketDay(c: any): ContractList {
    const contract = new ContractList(
        c.contract_id,
        c.status,
        c.market_day,
        undefined,
        undefined,
        c.buyer_statement_id,
        c.contract_type,
        new SaleStatement(c.buyer_statement_id),
        undefined
    )
    contract.quantity = c.quantity_loaded ?? 0
    contract.price_per_unit = c.price_per_kg ?? 0
    contract.delivery_date = c.dropoff_date
    const supplierOrg = c.supplier?.organisation
    contract.supplier_name = supplierOrg?.name
    contract.village = supplierOrg?.location?.village
    contract.subdistrict = supplierOrg?.location?.subdistrict
    contract.district = supplierOrg?.location?.district
    contract.province = supplierOrg?.location?.province
    contract.supplier_coordinates = supplierOrg?.position?.coordinates
    contract.weight_receipt_uri = c.dropoff?.weight_receipt_uri
    contract.truck_photo_uri = c.truck_photo?.trim() || undefined
    contract.truck_photo_coordinates = c.dropoff?.truck_photo_position?.coordinates
    contract.unilever_coordinates = c.unilever_land_plot?.position?.coordinates
    return contract
}

export function buildContractList(s: any): ContractList {
    return new ContractList(
        s.transport_crew_contract_id,
        s.transport_crew_contract.status,
        s.transport_crew_contract.market_day,
        s.sale_statement.line_items.map(s => buildSaleItem(s)),
        undefined,
        s.sale_statement.id,
        s.transport_crew_contract.contract_type,
        s.sale_statement ? buildSaleStatement(s.sale_statement) : undefined,
        s.transporter_transactor ? buildTransactorFromDetail(s.transporter_transactor) : undefined
    )
}